/**
 * P2.3 (owner): the sign-in page writes its own sentences, in both languages, on the form. The server only says "no" (a 422) or
 * "too many tries" (a 422 that carries `retry_after`, the seconds). Anything else (offline, a server fault, a blocked account)
 * keeps today's pop-up, so this returns null for it.
 */
export type SignInFailure = { kind: "mismatch" } | { kind: "throttle"; seconds: number } | { kind: "no_account" } | { kind: "waiting" };

export const signInFailure = (error: any): SignInFailure | null => {
  const res = error?.response;
  if (!res) return null;
  const seconds = Number(res.data?.retry_after ?? res.headers?.["retry-after"]);
  if (res.status === 429) {
    // The route-level limiter answers 429 (header Retry-After): the same countdown sentence, not "wrong e-mail or password".
    return { kind: "throttle", seconds: Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : 60 };
  }
  // Walkthrough #4/#5: the right password, but nothing to open. NO_ACCOUNT = approved, in no account (the no-account page);
  // a bare 403 (no body, on purpose) = a person staff did not approve: the "being reviewed" page, which promises nothing.
  if (res.status === 403) {
    if (res.data?.code === "NO_ACCOUNT") return { kind: "no_account" };
    if (res.data == null || res.data === "" || (typeof res.data === "object" && Object.keys(res.data).length === 0)) return { kind: "waiting" };
    return null;
  }
  if (res.status !== 422) return null;
  if (Number.isFinite(seconds) && seconds > 0) return { kind: "throttle", seconds: Math.ceil(seconds) };
  // 422 on the e-mail field is "wrong e-mail or password" (the server never says which, on purpose). A 422 about anything else
  // (language, device name) is not that sentence: it keeps the pop-up.
  return res.data?.errors?.email ? { kind: "mismatch" } : null;
};
